/**
 * 一个 WalletHub 代表的是一个钱包域，它代表的是用户的一个钱包插件或者一个钱包提供者
 */

import EventEmitter from 'eventemitter3';
import { ChainTypeList, ChainTypes } from './types';
import { AbstractWalletConnector } from './AbstractWalletConnector';
import { Web3ProviderConnector } from '../connectors/evm/Web3ProviderConnector';
import { TonConnector } from '../connectors/ton/TonConnector';
import { BtcConnector } from '../connectors/btc/BtcConnector';
import { SolanaConnector } from '../connectors/solana/SolanaConnector';
import { StacksConnector } from '../connectors/stx/StacksConnector';
import { debugLog } from '../../utils/common';
import { SuiConnector } from '../connectors/sui/SuiConnector';
import { TronConnector } from '../connectors/tron/TronConnector';

type WalletItem<Chain extends ChainTypes, T extends AbstractWalletConnector> = {
  address: string;
  chainId: number;
  chainType: Chain;
  data: Record<string, any>;
  connector: T;
};

export type ConnectOption = Partial<
  Record<
    ChainTypeList,
    {
      args?: any[];
    }
  >
> &
  Record<string, any> & {
    // 连接失败，是否停止下一个connector 连接
    breakWhenFail?: boolean;
  };

export type WalletMap = Partial<{
  BTC: WalletItem<ChainTypes.BTC, BtcConnector>;
  EVM: WalletItem<ChainTypes.EVM, Web3ProviderConnector>;
  SOL: WalletItem<ChainTypes.SOL, SolanaConnector>;
  SUI: WalletItem<ChainTypes.SUI, SuiConnector>;
  STX: WalletItem<ChainTypes.STX, StacksConnector>;
  TON: WalletItem<ChainTypes.TON, TonConnector>;
  TRON: WalletItem<ChainTypes.TRON, TonConnector>;
}>;

type WalletHubEvents = {
  walletChange: [WalletMap];
};

type WalletHubOption = {
  // 钱包名字，主要用于展示
  name: string;
  icon: string;
  // 用来区分钱包
  id: string;
  // 钱包icon
  // 标记钱包，用于给上层筛选（用来区分展示啥的）
  tags?: string[];
  // 下载地址
  installUrl?: string;

  // connector清单
  connectors: Array<AbstractWalletConnector | (() => Promise<AbstractWalletConnector>)>;

  isAvailable?: () => Promise<boolean>;
};

type Connection = {
  disconnect: () => void;
};

export class WalletHub extends EventEmitter<WalletHubEvents> {
  private _options: WalletHubOption;
  public connectors!: AbstractWalletConnector[];
  constructor(op: WalletHubOption) {
    super();
    this._options = Object.assign({}, op);
  }

  private _initFlag: Promise<void> | null = null;

  // 这个属性暂时不用，只是保存一下这个监听连接
  private _connection?: Connection;

  private async _initConnectors() {
    this.connectors = await Promise.all(this._options.connectors.map((c) => (typeof c === 'function' ? c() : c)));

    const disconnects: Array<() => void> = [];

    this.connectors.forEach((connector) => {
      const onWalletChange = () => {
        // TODO 减少emit 次数
        this.emit('walletChange', this.wallets);
      };
      connector.on('walletChange', onWalletChange);
      disconnects.push(() => connector.off('walletChange', onWalletChange));
    });

    this._connection = {
      disconnect: () => disconnects.forEach((d) => d()),
    };
  }

  public async initConnectors() {
    if (this._initFlag) {
      return this._initFlag;
    }
    this._initFlag = this._initConnectors().catch((err) => {
      console.error('init connector error', err);
      return;
    });
    return this._initFlag;
  }

  public get name() {
    return this._options.name;
  }

  public get id() {
    return this._options.id;
  }

  public get icon() {
    return this._options.icon;
  }
  public set icon(v: string) {
    this._options.icon = v;
  }

  public get tags() {
    return this._options.tags || [];
  }

  public get installUrl() {
    return this._options.installUrl;
  }

  public get isAvailable() {
    const isAvailable = this._options.isAvailable;
    if (isAvailable) {
      return isAvailable();
    }
    return this.initConnectors().then(() => {
      if (this.EVM) {
        return this.EVM.isAvailable();
      }
      if (this.connectors.length) {
        return this.connectors[0].isAvailable();
      }
      return false;
    });
  }

  public async connect(recovery = false, opts: ConnectOption = {}) {
    const { breakWhenFail = true } = opts;
    await this.initConnectors();
    for (const connector of this.connectors) {
      const available = await connector.isAvailable();
      if (!available) {
        continue;
      }
      if (!connector.address) {
        const args = opts?.[ChainTypes[connector.chainType]]?.args || [];
        const p = connector.connect(recovery, ...args);
        if (breakWhenFail && connector.chainType === ChainTypes.EVM) {
          // 这里只让evm报错，是为了 当okx 或 其它异构链的钱包，仅导入evm 私钥的时候能连成功（表现像Metaask一样），但是未来最好的理想情况是：
          // 设置 breakWhenFail 为 false，让所有的connector 都可以单独连接
          // 未来就可以加：TODO ，在opt 上加 参数，让单独去连某个 connector，并丰富 Web3WalletConnectButton 的需求和参数
          await p;
        } else {
          await p.catch((err) => {
            debugLog(`${connector.name} connect failed, recover mode: ${recovery}`, err);
          });
        }
      }
    }
  }

  public async disconnect() {
    await this.initConnectors();
    await Promise.all(
      this.connectors.map((c) =>
        c.disconnect().catch((err) => {
          console.error(`[${this.name}:${c.name}] disconnect error`, err);
        }),
      ),
    );
  }

  public getConnector<T extends AbstractWalletConnector = AbstractWalletConnector>(type: ChainTypeList): T | undefined {
    if (!this.connectors) {
      throw new Error('初始化connector 之前就调用了getConnector');
    }
    const typeConnectors = this.connectors.filter((c) => c.chainType === ChainTypes[type]);
    if (typeConnectors.length > 1) {
      console.warn(`钱包[${this.name}]`, `${type} 下有多个connector`);
    }
    return typeConnectors[0] as T;
  }

  public get BTC() {
    return this.getConnector<BtcConnector>('BTC');
  }

  public get SOL() {
    return this.getConnector<SolanaConnector>('SOL');
  }
  public get EVM() {
    return this.getConnector<Web3ProviderConnector>('EVM');
  }

  public get SUI() {
    return this.getConnector<SuiConnector>('SUI');
  }

  public get STX() {
    return this.getConnector<StacksConnector>('STX');
  }

  public get TON() {
    return this.getConnector<TonConnector>('TON');
  }

  public get TRON() {
    return this.getConnector<TronConnector>('TRON');
  }

  get evmChainId() {
    return this.EVM?.chainId ?? 0;
  }

  get hasAddress() {
    return Boolean(Object.keys(this.wallets).length);
  }

  get hasMultipleAddress() {
    return Object.keys(this.wallets).length > 1;
  }

  public get wallets(): WalletMap {
    if (!this.connectors) {
      return {};
    }
    const chainTypes = Object.keys(ChainTypes);

    return chainTypes.reduce((acc, type) => {
      const connector: AbstractWalletConnector = this[type];
      if (!connector?.address) {
        return acc;
      }
      acc[type] = {
        address: connector.address,
        chainId: connector.chainId,
        data: connector.data,
        chainType: connector.chainType,
        connector,
      };
      return acc;
    }, {}) as WalletMap;
  }
}
